/* =====================================================================================
   Today — data access for the landing page.

   Reads notices, events and the noticeboard straight from Postgres rather than through the
   seed overlay in db.ts. Those three are genuinely live, per-organisation and written by
   people, so the optimistic local-first model that suits Atlas records is the wrong shape:
   a notice that appears for its author and nobody else is worse than one that takes a
   moment to arrive.

   NOTHING HERE DECIDES WHO MAY WRITE. `canPublish` exists to avoid showing a compose box to
   someone who would only get an error, which is a courtesy, not a control. The control is
   the row level security in migration 0014. If this file said everyone was SLT, the database
   would still refuse them.
   ===================================================================================== */

import { BACKEND } from './db';
import { TENANT } from '@tenant';
import { explain as explainDb } from './dberrors';

export interface Notice {
  id: string;
  title: string;
  body: string;
  kind: 'announcement' | 'policy' | 'urgent' | 'celebration';
  pinned: boolean;
  publish_at: string;
  expires_at: string | null;
  requires_ack: boolean;
  ack_due: string | null;
  author_id: string;
  author_name?: string;
  acknowledged?: boolean;
}

export interface CompanyEvent {
  id: string;
  title: string;
  description: string | null;
  kind: 'company' | 'congress' | 'board' | 'deadline' | 'training';
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
  location: string | null;
  url: string | null;
}

export interface BoardPost {
  id: string;
  body: string;
  kind: 'general' | 'kudos' | 'question' | 'for_sale' | 'social';
  author_id: string;
  author_name?: string;
  created_at: string;
}

const live = () => BACKEND === 'supabase';

/** Does this member hold a role that may publish? Presentation only, see the header. */
export async function canPublish(): Promise<boolean> {
  if (!live()) return true; // demo mode: show the controls so the page can be evaluated
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('member_roles').select('role');
  return (data ?? []).some((r: any) => r.role === 'slt' || r.role === 'owner');
}

export async function fetchNotices(): Promise<Notice[]> {
  if (!live()) return (TENANT.seed.notice ?? []) as never;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('app').from('notices')
    .select('*')
    .order('pinned', { ascending: false })
    .order('publish_at', { ascending: false })
    .limit(30);
  if (error) throw new Error(error.message);
  const names = await (await import('./supabase')).nameOf();
  const now = Date.now();
  return (data ?? [])
    // Expiry is filtered here rather than in the query because the index deliberately has no
    // now() predicate: see the note in migration 0014.
    .filter((n: any) => !n.expires_at || new Date(n.expires_at).getTime() > now)
    .map((n: any) => ({ ...n, author_name: names(n.author_id) }));
}

export async function fetchNextEvents(limit = 4): Promise<CompanyEvent[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('app').from('events')
    .select('*')
    .gte('starts_at', new Date().toISOString())
    .order('starts_at', { ascending: true })
    .limit(limit);
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function fetchBoard(limit = 12): Promise<BoardPost[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('app').from('noticeboard_posts')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  const names = await (await import('./supabase')).nameOf();
  return (data ?? []).map((p: any) => ({ ...p, author_name: names(p.author_id) }));
}

async function currentMemberId(): Promise<string> {
  const { currentMember } = await import('./supabase');
  const m = await currentMember();
  if (!m?.id) throw new Error('No member record for the signed-in user.');
  return m.id;
}

export async function publishNotice(n: {
  title: string; body: string; kind: Notice['kind'];
  pinned?: boolean; requiresAck?: boolean; ackDue?: string | null;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('notices').insert({
    title: n.title, body: n.body, kind: n.kind, pinned: n.pinned ?? false,
    requires_ack: n.requiresAck ?? false,
    ack_due: n.requiresAck ? n.ackDue : null,
    author_id: await currentMemberId(),
    org_id: await currentOrgId(),
  });
  if (error) throw new Error(explain(error));
}

export async function addEvent(e: {
  title: string; kind: CompanyEvent['kind']; startsAt: string;
  endsAt?: string | null; location?: string; description?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('events').insert({
    title: e.title, kind: e.kind, starts_at: e.startsAt, ends_at: e.endsAt || null,
    location: e.location || null, description: e.description || null,
    created_by: await currentMemberId(),
    org_id: await currentOrgId(),
  });
  if (error) throw new Error(explain(error));
}

export async function postToBoard(body: string, kind: BoardPost['kind']) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('noticeboard_posts').insert({
    body, kind, author_id: await currentMemberId(), org_id: await currentOrgId(),
  });
  if (error) throw new Error(explain(error));
}

export async function acknowledgeNotice(noticeId: string, signedName: string) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('acknowledgements').insert({
    object_type: 'notice', object_id: noticeId, object_version: 1,
    member_id: await currentMemberId(), signed_name: signedName,
    org_id: await currentOrgId(),
  });
  if (error) throw new Error(explain(error));
}

async function currentOrgId(): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  if (!data?.id) throw new Error('No organisation visible to this member.');
  return data.id;
}

/* A refusal by RLS arrives as 42501 and reads like a bug unless it is translated. Here it
   almost always means exactly one thing, so say that thing. */
function explain(error: { code?: string; message: string }): string {
  if (error.code === '42501') {
    return 'Refused: publishing to the whole company is restricted to the senior leadership team.';
  }
  return error.message;
}

/* -------------------------------------------------------------------------------------
   Weather.

   Open-Meteo, chosen because it needs no API key. Any keyed service would put a credential
   in the browser bundle, where it is readable by anyone who opens developer tools, and a
   weather widget is not worth a leaked secret.

   Location comes from the browser only if the person allows it. The fallback is the
   registered office rather than a prompt they have to dismiss, and no coordinate is ever
   sent anywhere except to fetch a forecast.
   ------------------------------------------------------------------------------------- */
export interface Weather {
  tempC: number;
  feelsC: number;
  code: number;
  description: string;
  isDay: boolean;
  place: string;
}

const FALLBACK = { lat: 51.885, lon: -0.145, place: 'Hertfordshire' };

// WMO weather interpretation codes, grouped to what a person actually wants to know.
const WMO: Record<number, string> = {
  0: 'Clear', 1: 'Mainly clear', 2: 'Partly cloudy', 3: 'Overcast',
  45: 'Fog', 48: 'Freezing fog',
  51: 'Light drizzle', 53: 'Drizzle', 55: 'Heavy drizzle',
  56: 'Freezing drizzle', 57: 'Freezing drizzle',
  61: 'Light rain', 63: 'Rain', 65: 'Heavy rain',
  66: 'Freezing rain', 67: 'Freezing rain',
  71: 'Light snow', 73: 'Snow', 75: 'Heavy snow', 77: 'Snow grains',
  80: 'Rain showers', 81: 'Rain showers', 82: 'Violent rain showers',
  85: 'Snow showers', 86: 'Heavy snow showers',
  95: 'Thunderstorm', 96: 'Thunderstorm with hail', 99: 'Thunderstorm with hail',
};

/**
 * Coordinates, if the browser offers them quickly. Deliberately NOT awaited before the first
 * forecast: waiting on a permission prompt meant that anyone who ignored the dialog, or whose
 * browser blocked it silently, got no weather at all and no explanation. The fallback renders
 * straight away and the precise location upgrades it a moment later if allowed.
 */
function position(timeout = 6000): Promise<{ lat: number; lon: number; place: string } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation || !window.isSecureContext) return resolve(null);
    let settled = false;
    const done = (v: { lat: number; lon: number; place: string } | null) => {
      if (!settled) { settled = true; resolve(v); }
    };
    // Belt and braces: some browsers never call either callback if the prompt is dismissed.
    setTimeout(() => done(null), timeout);
    navigator.geolocation.getCurrentPosition(
      (p) => done({ lat: p.coords.latitude, lon: p.coords.longitude, place: 'Your location' }),
      () => done(null),
      { timeout, maximumAge: 900_000 },
    );
  });
}

/** Weather for a given point. Returns null only if the network call itself fails. */
export async function fetchWeatherAt(
  lat: number, lon: number, place: string,
): Promise<Weather | null> {
  try {
    const url =
      `https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(3)}` +
      `&longitude=${lon.toFixed(3)}` +
      `&current=temperature_2m,apparent_temperature,is_day,weather_code&timezone=auto`;
    const r = await fetch(url);
    if (!r.ok) return null;
    const j = await r.json();
    const c = j.current;
    return {
      tempC: Math.round(c.temperature_2m),
      feelsC: Math.round(c.apparent_temperature),
      code: c.weather_code,
      description: WMO[c.weather_code] ?? 'Unsettled',
      isDay: c.is_day === 1,
      place,
    };
  } catch {
    // A weather widget must never be the reason the landing page fails to render.
    return null;
  }
}

/**
 * Two stages. The registered office renders immediately so there is always something on
 * screen, then the browser's own location replaces it if the person allows it. Previously
 * this waited for the permission prompt and showed nothing at all when it never resolved,
 * which is why the widget appeared to be missing entirely.
 */
export async function fetchWeather(
  onUpgrade?: (w: Weather) => void,
): Promise<Weather | null> {
  const first = await fetchWeatherAt(FALLBACK.lat, FALLBACK.lon, FALLBACK.place);

  void position().then(async (p) => {
    if (!p || !onUpgrade) return;
    const better = await fetchWeatherAt(p.lat, p.lon, p.place);
    if (better) onUpgrade(better);
  });

  return first;
}

/* -------------------------------------------------------------------------------------
   The diary.

   app.diary_entries is built by app.refresh_diary(), which aggregates every dated
   obligation in the system: patent deadlines from Docket, reimbursement submission windows
   from Atlas, contract notice periods, grant claim windows. The point of one diary is that
   the failures which actually hurt are silent, and they are silent in different modules.

   This replaces a hardcoded list that lived in the interface. That list held real dates, but
   a deadline nobody can add to or complete is a poster, not a diary.
   ------------------------------------------------------------------------------------- */

export interface DiaryEntry {
  id: string;
  title: string;
  detail: string | null;
  category: string;
  due_date: string;
  severity: 'critical' | 'high' | 'normal' | 'low';
  status: string;
  source_system: string;
  object_type: string | null;
  object_id: string | null;
}

export async function fetchDiary(): Promise<DiaryEntry[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('app').from('diary_entries')
    .select('*')
    .eq('status', 'open')
    .order('due_date', { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
}

/** Rebuild from the source modules. Safe to call: it only replaces non-manual open entries. */
export async function refreshDiary() {
  const { supabase } = await import('./supabase');
  const { data: org } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  const { error } = await supabase().schema('app').rpc('refresh_diary', { p_org_id: org?.id });
  if (error) throw new Error(error.message);
}

export async function completeDiaryEntry(id: string) {
  const { supabase, currentMember } = await import('./supabase');
  const me = await currentMember();
  const { error } = await supabase().schema('app').from('diary_entries')
    .update({ status: 'done', completed_at: new Date().toISOString(), completed_by: me?.id })
    .eq('id', id);
  if (error) throw new Error(error.message);
}


/* ---------------------------------------------------------------------------------
   Taking a notice down.

   Two actions because they are two problems. Retracting withdraws it from every screen and
   keeps the row and any acknowledgements: the record of what people were told, and what they
   signed, survives. Deleting destroys the content, for the case where the content itself is
   the problem, and leaves only a record that a removal happened.
   --------------------------------------------------------------------------------- */

export async function retractNotice(noticeId: string, reason: string): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc('retract_notice', {
    p_notice_id: noticeId, p_reason: reason,
  });
  if (error) throw new Error(explainDb(error.message, 'take this notice down'));
  return data as string;
}

export async function deleteNotice(noticeId: string, reason: string): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc('delete_notice', {
    p_notice_id: noticeId, p_reason: reason,
  });
  if (error) throw new Error(explainDb(error.message, 'delete this notice'));
  return data as string;
}
